import { useMutation, useQueryClient } from 'react-query';

import axiosClient from './axios-client';
import { ApiError, ApiResponse, useApiMutation, useApiQuery } from './useApi';
import { invalidateCrmQueries, useCrmCollection } from './useCrm';
import type { EventType } from '../types/prospectingEvergreen';
import type { AssumptionSpec } from './useSalonDiagnostic';
import type { DiagnosticAssumptions, DiagnosticInputs, DiagnosticPractice } from '../pages/public-salon-diagnostic/diagnosticScore';

// #################################################
//                      TYPES
// #################################################

export type SalonDemoMode = 'scheduled' | 'lemcal' | 'none';
export type SalonTemperature = 'boiling' | 'hot' | 'warm' | 'cold';
/** Palier tarifaire pressenti — les quatre offres publiques de TriggerFlow. */
export type SalonOffer = 'decouverte' | 'communication' | 'conversion' | 'all_inclusive';

export type CrmEvent = {
    id: number;
    name: string;
    /** Webinaires seulement : deux textes libres affichés sur la page publique. */
    subtitle: string | null;
    description: string | null;
    slug: string;
    starts_on: string;
    ends_on: string;
    timezone: string;
    location: string | null;
    stand: string | null;
    lemcal_url: string | null;
    is_active: boolean;
    accepts_self_service: boolean;
    type: CrmEventType;
    /** URL de la page libre-service (porte le jeton) : écrans équipe uniquement. */
    public_url: string;
    /** URL du QR des flyers (porte le jeton) : écrans équipe uniquement, toujours renvoyée par l'API. */
    flyer_url: string;
    /** Lien d'équipe (tablettes, sans compte, protégé par le PIN du salon). */
    staff_url: string;
    /** Diagnostic express du stand : même jeton et même PIN que le lien d'équipe. */
    diagnostic_url: string;
    has_staff_pin: boolean;
    /** Début d'un webinaire, heure du lieu (`YYYY-MM-DDTHH:mm`). */
    starts_at: string | null;
    duration_minutes: number | null;
    meeting_url: string | null;
    replay_url: string | null;
    /** Page d'inscription publique d'un webinaire ou d'un formulaire. */
    registration_url: string;
    is_registration_open: boolean;
    /** Champs demandés par un formulaire ; null pour un salon ou un webinaire. */
    form_fields: CrmFormFields | null;
    submit_label: string | null;
    success_message: string | null;
    default_assigned_user: { id: number; name: string } | null;
    leads_count?: number;
};

export type CrmEventType = 'salon' | 'webinar' | 'form';

/** Visibilité d'un champ de formulaire public ; l'e-mail est toujours requis. */
export type CrmFormFieldVisibility = 'required' | 'optional' | 'hidden';

export type CrmFormFields = Record<'firstname' | 'lastname' | 'phone' | 'establishment', CrmFormFieldVisibility>;

export type CrmEventPayload = {
    name: string;
    subtitle?: string | null;
    description?: string | null;
    slug: string;
    /** Les types de prospection (salon visité, événement organisé) s'ajoutent à ceux du module. */
    type?: CrmEventType | EventType;
    /** Lien d'inscription externe (https). */
    registration_url?: string | null;
    /** Facultatifs pour un formulaire : le serveur prend la date du jour. */
    starts_on?: string;
    ends_on?: string;
    starts_at?: string | null;
    duration_minutes?: number | null;
    meeting_url?: string | null;
    replay_url?: string | null;
    timezone?: string | null;
    location?: string | null;
    stand?: string | null;
    lemcal_url?: string | null;
    default_assigned_user_id?: number | null;
    is_active?: boolean;
    /** Code du lien d'équipe ; obligatoire à la création, facultatif ensuite (inchangé si vide). */
    staff_pin?: string | null;
    form_fields?: CrmFormFields | null;
    submit_label?: string | null;
    success_message?: string | null;
};

export type CrmInterest = {
    id: number;
    name: string;
    slug: string;
};

/**
 * Comment une rencontre a été saisie.
 *
 * `registration` (inscription à un webinaire) manquait ici alors que
 * `EventLeadsTab` l'affiche déjà via son dictionnaire de libellés — piège de
 * ce chantier : une valeur backend qui existe, un dictionnaire qui la connaît,
 * mais un type qui ne l'exhaustait pas. Nécessaire ici : l'onglet « Toutes les
 * demandes » agrège aussi les webinaires.
 */
export type SalonLeadChannel = 'team' | 'partner' | 'self_service' | 'flyer' | 'registration';

export type CrmEventLead = {
    id: number;
    event_id: number;
    channel: SalonLeadChannel;
    temperature: SalonTemperature | null;
    /** Palier tarifaire pressenti, saisi en même temps que la température. */
    offer: SalonOffer | null;
    notes: string | null;
    demo_mode: SalonDemoMode;
    demo_at?: string | null;
    /** Démo réservée sur lemcal depuis le stand : prise, mais sans heure connue. */
    demo_via_lemcal?: boolean;
    attendance: 'present' | 'absent' | null;
    finalized_at: string | null;
    captured_at: string;
    client_uuid: string | null;
    prospect?: { id: number; name: string; city: string | null } | null;
    facility?: { id: number; name: string } | null;
    contact?: { id: number; name: string | null; role: string | null; email: string | null; phone: string | null } | null;
    captured_by?: { id: number; name: string } | null;
    /** Posé en `whenLoaded` côté API : absent sur `ListSalonLeadsController` (un seul événement déjà connu de la page). */
    event?: { id: number; name: string; type: CrmEventType };
};

/** Saisie équipe telle qu'envoyée à l'API (et conservée dans la file de l'appareil). */
export type SalonCapturePayload = {
    client_uuid: string;
    /** Saisie prestataire d'origine, quand une rencontre équipe en découle. */
    intake_uuid?: string;
    prospect_id?: number | null;
    establishment: { name: string; city?: string | null; nb_rooms?: number | null; current_pms?: string | null };
    contact: { firstname?: string | null; lastname?: string | null; role?: string | null; email?: string | null; phone?: string | null };
    interest_ids: number[];
    temperature?: SalonTemperature | null;
    /** Palier tarifaire pressenti, facultatif dès la page 1. */
    offer?: SalonOffer | null;
    notes?: string | null;
    /** `via_lemcal` : « Créneau fixé » réservé sur le calendrier lemcal du salon, heure inconnue. */
    demo: { mode: SalonDemoMode; scheduled_at?: string | null; duration_minutes?: number | null; via_lemcal?: boolean };
};

export type SalonCaptureOutcome = 'created' | 'merged' | 'client' | 'replayed';

export type SalonCaptureResponse = {
    outcome: SalonCaptureOutcome;
    lead: CrmEventLead;
};

export type SalonLookupMatch = {
    type: 'prospect' | 'client';
    id: number;
    name: string;
    city: string | null;
    /** `email` : certain, la saisie complétera cette fiche. `name` : simple suggestion. */
    matched_on: 'email' | 'name';
    stage: string | null;
    assigned_user: string | null;
};

// #################################################
//                  ÉQUIPE (CRM)
// #################################################

export function useCrmEvents() {
    return useCrmCollection<CrmEvent>('/crm/events');
}

export function useCrmEvent(eventId: number) {
    return useApiQuery<{ data: CrmEvent }>(eventId > 0 ? `/crm/events/${eventId}` : '');
}

export function useCreateCrmEvent() {
    const queryClient = useQueryClient();

    return useApiMutation<CrmEvent, unknown, CrmEventPayload>('post', () => '/crm/events', {
        onSuccess: () => invalidateCrmQueries(queryClient),
    });
}

export function useUpdateCrmEvent() {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: CrmEvent }, unknown, CrmEventPayload & { id: number }>(
        'put',
        ({ id }) => `/crm/events/${id}`,
        { onSuccess: () => invalidateCrmQueries(queryClient) },
    );
}

export function useCrmInterests() {
    return useCrmCollection<CrmInterest>('/crm/interests');
}

/**
 * Un centre d'intérêt vu par l'écran « Centres d'intérêt » : ce que `[Ressources]`
 * insère dans les relances. Le lien, s'il existe, remplace le paragraphe.
 * Jamais servi aux pages publiques des salons (elles reçoivent `CrmInterest`).
 */
export type CrmInterestSettings = CrmInterest & {
    is_active: boolean;
    resource_label: string | null;
    resource_url: string | null;
    email_snippet: string | null;
};

export type CrmInterestSettingsPayload = Pick<CrmInterestSettings, 'id' | 'resource_label' | 'resource_url' | 'email_snippet'>;

const INTEREST_SETTINGS_URL = '/crm/interest-settings';

export function useCrmInterestSettings() {
    return useCrmCollection<CrmInterestSettings>(INTEREST_SETTINGS_URL);
}

export function useUpdateCrmInterest() {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: CrmInterestSettings }, ApiError, CrmInterestSettingsPayload>(
        'put',
        ({ id }) => `${INTEREST_SETTINGS_URL}/${id}`,
        { onSuccess: () => queryClient.invalidateQueries([INTEREST_SETTINGS_URL]) },
    );
}

export function useCrmEventLeads(eventId: number, mine = false) {
    return useCrmCollection<CrmEventLead>(
        eventId > 0 ? `/crm/events/${eventId}/leads${mine ? '?mine=1' : ''}` : '',
    );
}

/** Accès au lien d'équipe d'un salon : jeton de l'URL + PIN saisi sur l'appareil. */
export type SalonTeamAccess = { token: string; pin: string };

function pinHeader(access: SalonTeamAccess) {
    return { headers: { 'X-Salon-Pin': access.pin } };
}

/**
 * « Déjà connu ? » : ne part qu'avec une adresse complète ou un nom d'au moins
 * trois lettres, pour ne pas interroger l'API à chaque frappe.
 */
export function useSalonLookup(params: { email?: string; name?: string; city?: string }, access: SalonTeamAccess) {
    const email = params.email?.trim() ?? '';
    const name = params.name?.trim() ?? '';
    const enabled = /\S+@\S+\.\S+/.test(email) || name.length >= 3;
    const query = new URLSearchParams();

    if (email) query.set('email', email);
    if (name) query.set('name', name);
    if (params.city?.trim()) query.set('city', params.city.trim());

    const result = useApiQuery<{ data: SalonLookupMatch[] }>(
        enabled ? `/public-salon-team/${access.token}/lookup?${query.toString()}` : '',
        pinHeader(access),
        { staleTime: 30_000, keepPreviousData: true, retry: false },
    );

    // `keepPreviousData` garde le dernier résultat même requête désactivée : sans
    // ce garde, l'encart « déjà connu » survivrait au formulaire vidé.
    return { ...result, data: enabled ? result.data : undefined };
}

// #################################################
//              LIEN D'ÉQUIPE (PUBLIC + PIN)
// #################################################

export type SalonTeamInfo = {
    /** `lemcal_url` : ouvert par « Créneau fixé » ; `has_lemcal` reste pour les écrans déjà chargés. */
    event: { name: string; location: string | null; has_lemcal: boolean; lemcal_url?: string | null; timezone: string };
    interests: CrmInterest[];
    team: { id: number; name: string }[];
};

/** Page 3 : le commercial se désigne, qualifie et complète la rencontre. */
export type SalonFinalizePayload = {
    seller_id: number;
    temperature?: SalonTemperature | null;
    /** Palier tarifaire pressenti, facultatif. */
    offer?: SalonOffer | null;
    notes?: string | null;
    establishment?: { nb_rooms?: number | null; current_pms?: string | null };
};

export function useSalonTeam(access: SalonTeamAccess) {
    return useApiQuery<{ data: SalonTeamInfo }>(
        access.token && access.pin ? `/public-salon-team/${access.token}` : '',
        pinHeader(access),
        { retry: false, staleTime: 5 * 60_000 },
    );
}

// #################################################
//              LIBRE-SERVICE (PUBLIC)
// #################################################

export type PublicSalon = {
    event: { name: string; location: string | null; stand: string | null; has_demo_booking: boolean; lemcal_url: string | null };
    interests: CrmInterest[];
    /** Diagnostic express du visiteur : pratiques et hypothèses de l'équipe (lecture seule). */
    diagnostic?: { practices: DiagnosticPractice[]; assumptions: Record<keyof DiagnosticAssumptions, AssumptionSpec> };
};

/**
 * Diagnostic et coordonnées du visiteur, envoyés en une fois. `send_email` est
 * le geste « Recevoir mon diagnostic par e-mail » (il vaut accord) ;
 * `demo_via_lemcal` celui de « Prendre un rendez-vous ».
 */
export type PublicSalonDiagnosticPayload = {
    client_uuid: string;
    inputs: DiagnosticInputs;
    establishment: { name: string; city?: string | null };
    contact: { firstname: string; lastname: string; role?: string | null; email: string; phone?: string | null };
    interest_ids: number[];
    send_email: boolean;
    demo_via_lemcal: boolean;
    /** Champ piège : toujours vide chez un humain. */
    website: string;
};

export type PublicSalonPayload = {
    client_uuid: string;
    establishment: { name: string; city?: string | null };
    contact: { firstname: string; lastname: string; role?: string | null; email: string; phone?: string | null };
    interest_ids: number[];
    wants_demo: boolean;
    /** Champ piège : toujours vide chez un humain. */
    website: string;
};

export function usePublicSalon(token: string) {
    return useApiQuery<{ data: PublicSalon }>(token ? `/public-salon/${token}` : '', {}, { retry: false });
}

/**
 * `email_sent` : faux si un garde-fou a joué (boîte déjà servie dans les 24 h,
 * tous salons, ou désinscrite, salon fini depuis plus de 7 jours, trop
 * d'envois pour ce salon dans l'heure). Le diagnostic est enregistré quand même.
 */
export function useSubmitPublicSalonDiagnostic(token: string) {
    return useApiMutation<{ ok: boolean; email_sent: boolean }, unknown, PublicSalonDiagnosticPayload>('post', () => `/public-salon/${token}/diagnostics`);
}

// #################################################
//         LIBRE-SERVICE — QR DES FLYERS (PUBLIC)
// #################################################

export interface PublicSalonGuide { id: number; name: string }

export interface PublicSalonFlyer {
    event: { name: string; location: string | null; stand: string | null; has_demo_booking: boolean; lemcal_url: string | null };
    interests: { id: number; name: string }[];
    guides: PublicSalonGuide[];
}

export interface PublicSalonFlyerPayload extends PublicSalonPayload {
    lead_magnet_ids: number[];
}

export function usePublicSalonFlyer(token: string) {
    return useApiQuery<{ data: PublicSalonFlyer }>(token ? `/public-salon-flyer/${token}` : '', {}, { retry: false });
}

export function useSubmitPublicSalonFlyerLead(token: string) {
    return useApiMutation<{ ok: boolean }, unknown, PublicSalonFlyerPayload>('post', () => `/public-salon-flyer/${token}/leads`);
}

// #################################################
//              SÉQUENCE D'UN ÉVÉNEMENT
// #################################################

export type SequenceAnchor = 'captured' | 'event_start' | 'event_end' | 'demo';
export type SequenceChannel = 'email' | 'sms' | 'sms_or_email';
export type SequenceAudience = 'all' | 'demo_scheduled' | 'no_demo' | 'attended' | 'absent' | 'met_at_stand' | 'from_flyer';
export type SequenceMessageStatus = 'planned' | 'sending' | 'sent' | 'skipped' | 'canceled' | 'failed';

export type SequenceStep = {
    id: number;
    event_id: number;
    position: number;
    name: string;
    is_active: boolean;
    anchor: SequenceAnchor;
    offset_minutes: number;
    send_time: string | null;
    channel: SequenceChannel;
    audience: SequenceAudience;
    subject: string | null;
    body: string | null;
    sms_body: string | null;
    drop_if_postponed: boolean;
    /** Part même un jour où le contact a déjà reçu un e-mail (confirmation, rappel avant RDV). */
    is_time_critical: boolean;
    stats?: Record<SequenceMessageStatus, number>;
};

export type SequenceStepPayload = Omit<SequenceStep, 'id' | 'event_id' | 'position' | 'is_time_critical' | 'stats'>;

export type SequencePreview = {
    subject: string;
    text: string;
    html: string;
    sms: string;
    sample: boolean;
    lead_label: string;
};

export function useEventSteps(eventId: number) {
    return useCrmCollection<SequenceStep>(eventId > 0 ? `/crm/events/${eventId}/steps` : '');
}

export function useCreateStep(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<SequenceStep, unknown, SequenceStepPayload>('post', () => `/crm/events/${eventId}/steps`, {
        onSuccess: () => invalidateCrmQueries(queryClient),
    });
}

export function useUpdateStep(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: SequenceStep }, unknown, SequenceStepPayload & { id: number }>(
        'put',
        ({ id }) => `/crm/events/${eventId}/steps/${id}`,
        { onSuccess: () => invalidateCrmQueries(queryClient) },
    );
}

export function useDeleteStep(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<void, unknown, { id: number }>('delete', ({ id }) => `/crm/events/${eventId}/steps/${id}`, {
        onSuccess: () => invalidateCrmQueries(queryClient),
    });
}

export function useReorderSteps(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<void, unknown, { step_ids: number[] }>('post', () => `/crm/events/${eventId}/steps/reorder`, {
        onSuccess: () => invalidateCrmQueries(queryClient),
    });
}

/** Aperçu du brouillon en cours d'édition, rendu côté serveur sans rien enregistrer. */
export function useStepPreview(eventId: number, stepId: number) {
    return useApiMutation<SequencePreview, unknown, { subject?: string | null; body?: string | null; sms_body?: string | null }>(
        'post',
        () => `/crm/events/${eventId}/steps/${stepId}/preview`,
    );
}

export function useSendStepTest(eventId: number, stepId: number) {
    return useApiMutation<{ message: string }, unknown, Record<string, never>>('post', () => `/crm/events/${eventId}/steps/${stepId}/test`);
}

// #################################################
//              WEBINAIRES
// #################################################

export function useMarkAttendance(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: CrmEventLead }, unknown, { leadId: number; attendance: 'present' | 'absent' | null }>(
        'put',
        ({ leadId }) => `/crm/events/${eventId}/leads/${leadId}/attendance`,
        { onSuccess: () => invalidateCrmQueries(queryClient) },
    );
}

export function useMarkUnmarkedAbsent(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<{ marked: number }, unknown, Record<string, never>>(
        'post',
        () => `/crm/events/${eventId}/attendance/absent-unmarked`,
        { onSuccess: () => invalidateCrmQueries(queryClient) },
    );
}

export type PublicWebinar = {
    event: {
        type: 'webinar' | 'form';
        name: string;
        subtitle: string | null;
        description: string | null;
        /** Nul pour un formulaire, qui n'a pas de date. */
        starts_at: string | null;
        duration_minutes: number | null;
        timezone: string;
        is_open: boolean;
        fields: CrmFormFields;
        submit_label: string | null;
        success_message: string | null;
    };
};

export type PublicWebinarPayload = {
    client_uuid: string;
    // Formulaire simplifié : ni établissement, ni poste, ni centres d'intérêt.
    // `establishment.name` est déduit côté back du nom complet du contact
    // quand il est absent (voir PublicWebinarController::establishmentName()).
    // Un formulaire n'envoie que les champs qu'il affiche (le serveur refuse les autres).
    contact: { firstname?: string | null; lastname?: string | null; email: string; phone?: string | null };
    establishment?: { name: string | null };
    /** Champ piège : toujours vide chez un humain. */
    website: string;
};

export function usePublicWebinar(token: string) {
    return useApiQuery<{ data: PublicWebinar }>(token ? `/public-webinar/${token}` : '', {}, { retry: false });
}

export function useRegisterWebinar(token: string) {
    return useApiMutation<{ ok: boolean }, unknown, PublicWebinarPayload>('post', () => `/public-webinar/${token}/registrations`);
}

// #################################################
//              BILAN D'UN ÉVÉNEMENT
// #################################################

export type EventReport = {
    leads: {
        total: number;
        prospects: number;
        clients: number;
        by_channel: Partial<Record<SalonLeadChannel, number>>;
        by_temperature: Partial<Record<SalonTemperature, number>>;
    };
    attendance: { present: number; absent: number; unmarked: number } | null;
    demos: { booked: number; done: number; no_show: number; canceled: number; awaiting_outcome: number };
    replies: number;
    outcomes: { won: number; won_amount: number; lost: number };
    messages: Partial<Record<SequenceMessageStatus, number>>;
    sellers: { name: string; leads: number; demos: number; won: number }[];
};

export function useEventReport(eventId: number) {
    return useApiQuery<{ data: EventReport }>(eventId > 0 ? `/crm/events/${eventId}/report` : '');
}

/** Compteurs d'audience de la page « Calendrier marketing » du site (QR du flyer). */
export type CalendarStatsCounts = {
    /** Visites distinctes (identifiant aléatoire de visite tiré par le site). */
    visits: number;
    /** Visites dont au moins un geste porte `utm_medium=flyer`. */
    qr_visits: number;
    page_views: number;
    copies: number;
    downloads: number;
    activate_clicks: number;
    client_clicks: number;
    demo_clicks: number;
};

/** Thème le plus retenu (copies, téléchargements, changements de thème). */
export type CalendarTopTheme = { code: string; label: string; count: number };

export type EventCalendarStats = {
    period: { from: string | null; to: string | null };
    totals: CalendarStatsCounts & { callbacks: number; top_theme: CalendarTopTheme | null };
    days: (CalendarStatsCounts & { date: string; callbacks: number })[];
    temps_forts: (CalendarStatsCounts & { code: string; name: string; top_theme: CalendarTopTheme | null })[];
};

export type CalendarStatsRange = { from?: string; to?: string };

/** Rafraîchi chaque minute : l'onglet sert d'écran de suivi pendant le salon. */
export function useEventCalendarStats(eventId: number, range: CalendarStatsRange) {
    const query = new URLSearchParams(Object.entries(range).filter((entry): entry is [string, string] => Boolean(entry[1]))).toString();
    const url = eventId > 0 ? `/crm/events/${eventId}/calendar-stats${query ? `?${query}` : ''}` : '';

    return useApiQuery<{ data: EventCalendarStats }>(url, {}, { refetchInterval: 60_000, keepPreviousData: true });
}

// #################################################
//         LIEN PRESTATAIRE (PUBLIC, SANS PIN)
// #################################################

export type SalonPartnerInfo = {
    event: { name: string; location: string | null; stand?: string | null; timezone: string };
    partner: { name: string; company: string | null };
    interests: CrmInterest[];
};

export type SalonIntakePayload = {
    client_uuid: string;
    establishment_name: string;
    city?: string | null;
    nb_rooms?: number | null;
    current_pms?: string | null;
    contact_firstname?: string | null;
    contact_lastname?: string | null;
    contact_role?: string | null;
    contact_email?: string | null;
    contact_phone?: string | null;
    is_decision_maker: boolean;
    is_unhappy_with_tools: boolean;
    asked_for_demo: boolean;
    verdict: 'demo' | 'nurturing';
    handoff?: 'accompanying' | 'callback' | null;
    notes?: string | null;
    interest_ids: number[];
};

/** Zone d'affichage calculée par le serveur : le seuil de bascule est une règle métier. */
export type SalonIntakeZone = 'arriving' | 'callback' | 'stale' | 'nurturing';

export type SalonIntakeRow = {
    id: number;
    establishment_name: string;
    city: string | null;
    nb_rooms: number | null;
    current_pms: string | null;
    contact_name: string | null;
    contact_firstname: string | null;
    contact_lastname: string | null;
    contact_role: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    is_decision_maker: boolean;
    is_unhappy_with_tools: boolean;
    asked_for_demo: boolean;
    verdict: 'demo' | 'nurturing';
    handoff: 'accompanying' | 'callback' | null;
    notes: string | null;
    status: 'pending' | 'validated' | 'rejected';
    rejected_reason: string | null;
    captured_at: string;
    client_uuid: string;
    partner?: { id: number; name: string; company: string | null };
    interest_ids?: number[];
    duplicate_ids: number[];
    zone: SalonIntakeZone;
};

export function useSalonPartner(token: string) {
    return useApiQuery<{ data: SalonPartnerInfo }>(
        token ? `/public-salon-partner/${token}` : '',
        {},
        { retry: false, staleTime: 5 * 60_000 },
    );
}

/** « Déjà connu ? » côté prestataire : évite de vendre TriggerFlow à un client. */
export function usePartnerLookup(params: { email?: string; name?: string; city?: string }, token: string) {
    const email = params.email?.trim() ?? '';
    const name = params.name?.trim() ?? '';
    const enabled = /\S+@\S+\.\S+/.test(email) || name.length >= 3;
    const query = new URLSearchParams();

    if (email) query.set('email', email);
    if (name) query.set('name', name);
    if (params.city?.trim()) query.set('city', params.city.trim());

    const result = useApiQuery<{ data: SalonLookupMatch[] }>(
        enabled ? `/public-salon-partner/${token}/lookup?${query.toString()}` : '',
        {},
        { staleTime: 30_000, keepPreviousData: true, retry: false },
    );

    return { ...result, data: enabled ? result.data : undefined };
}

// #################################################
//      FILE D'ATTENTE DES SAISIES (LIEN ÉQUIPE)
// #################################################

/**
 * La file, rafraîchie toutes les 15 secondes.
 *
 * Pas 60 : quelqu'un marche vers le stand, et une saisie vue trois minutes trop
 * tard est un échec.
 */
export function useSalonIntakeQueue(access: SalonTeamAccess) {
    return useApiQuery<{ data: SalonIntakeRow[] }>(
        access.token && access.pin ? `/public-salon-team/${access.token}/intakes` : '',
        pinHeader(access),
        { refetchInterval: 15_000, refetchOnWindowFocus: true, retry: false },
    );
}

/**
 * Mutations de la file — **pas** via `useApiMutation`.
 *
 * `useApiMutation` écrit ses en-têtes en dur (`headers: data instanceof FormData ? … : {}`
 * dans `useApi.ts`) et ignore donc tout en-tête passé en options : le
 * `X-Salon-Pin` ne partirait jamais et chaque appel reviendrait en 401. On passe
 * par `axiosClient` directement, comme le fait déjà `useSalonCaptureQueue`.
 */
function useIntakeAction<T>(access: SalonTeamAccess, path: (clientUuid: string) => string) {
    const queryClient = useQueryClient();

    return useMutation<T, ApiError, { clientUuid: string; reason?: string }>(
        async ({ clientUuid, reason }) => {
            const response = await axiosClient.post(
                `/public-salon-team/${access.token}/${path(clientUuid)}`,
                reason === undefined ? {} : { reason },
                { headers: { 'X-Salon-Pin': access.pin } },
            );

            return response.data;
        },
        { onSuccess: () => queryClient.invalidateQueries([`/public-salon-team/${access.token}/intakes`]) },
    );
}

export function useRejectSalonIntake(access: SalonTeamAccess) {
    return useIntakeAction<{ data: SalonIntakeRow }>(access, (uuid) => `intakes/${uuid}/reject`);
}

export function useReopenSalonIntake(access: SalonTeamAccess) {
    return useIntakeAction<{ data: SalonIntakeRow }>(access, (uuid) => `intakes/${uuid}/reopen`);
}

/** Valide un lead de nurturing en un geste : rien à compléter de vive voix. */
export function useValidateSalonIntake(access: SalonTeamAccess) {
    return useIntakeAction<{ data: SalonIntakeRow; lead_id: number }>(access, (uuid) => `intakes/${uuid}/validate`);
}

// #################################################
//     PRESTATAIRES D'UN ÉVÉNEMENT (CRM)
// #################################################

export type CrmEventPartnerRow = {
    id: number;
    name: string;
    company: string | null;
    is_active: boolean;
    token: string;
    capture_url: string;
    intakes_count: number;
    validated_count: number;
    created_at: string | null;
};

export function useEventPartners(eventId: number) {
    return useCrmCollection<CrmEventPartnerRow>(`/crm/events/${eventId}/partners`);
}

export function useCreateEventPartner(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: CrmEventPartnerRow }, ApiError, { name: string; company?: string }>(
        'post',
        () => `/crm/events/${eventId}/partners`,
        { onSuccess: () => queryClient.invalidateQueries([`/crm/events/${eventId}/partners`]) },
    );
}

export function useUpdateEventPartner(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: CrmEventPartnerRow }, ApiError, { id: number; is_active?: boolean; name?: string; company?: string }>(
        'put',
        (vars) => `/crm/events/${eventId}/partners/${vars.id}`,
        { onSuccess: () => queryClient.invalidateQueries([`/crm/events/${eventId}/partners`]) },
    );
}

// #################################################
//     ÉQUIPE DU STAND D'UN SALON (CRM)
// #################################################

export type CrmEventStaffMember = {
    id: number;
    firstname: string;
    lastname: string | null;
    name: string;
    leads_count: number;
};

/** Qui est proposé dans « Qui tient la tablette ? » : collaborateurs cochés et personnes propres au salon. */
export type CrmEventTeam = {
    user_ids: number[];
    staff_members: CrmEventStaffMember[];
    available_users: { id: number; name: string }[];
};

const eventTeamUrl = (eventId: number) => `/crm/events/${eventId}/team`;

export function useEventTeam(eventId: number) {
    return useApiQuery<{ data: CrmEventTeam }>(eventTeamUrl(eventId));
}

export function useUpdateEventTeamUsers(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: CrmEventTeam }, ApiError, { user_ids: number[] }>(
        'put',
        () => `${eventTeamUrl(eventId)}/users`,
        { onSuccess: () => queryClient.invalidateQueries([eventTeamUrl(eventId)]) },
    );
}

export function useCreateEventStaffMember(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: CrmEventStaffMember }, ApiError, { firstname: string; lastname?: string }>(
        'post',
        () => `${eventTeamUrl(eventId)}/staff-members`,
        { onSuccess: () => queryClient.invalidateQueries([eventTeamUrl(eventId)]) },
    );
}

export function useDeleteEventStaffMember(eventId: number) {
    const queryClient = useQueryClient();

    return useApiMutation<null, ApiError, { id: number }>(
        'delete',
        (vars) => `${eventTeamUrl(eventId)}/staff-members/${vars.id}`,
        { onSuccess: () => queryClient.invalidateQueries([eventTeamUrl(eventId)]) },
    );
}

// #################################################
//   ÉCRAN « DEMANDES » DU CRM (VUE DURABLE DU SAS)
// #################################################

/**
 * Une saisie de prestataire, vue depuis le CRM (`CrmIntakeQueueController`).
 *
 * `event` est toujours présent ici : le contrôleur eager-charge la relation,
 * contrairement au lien d'équipe où la page est déjà bornée à un salon.
 * `zone` existe côté API (posée à `null`, jamais calculée dans ce contrôleur)
 * mais n'a pas de sens à froid dans le CRM — volontairement absente d'ici.
 */
export type CrmEventIntakeRow = {
    id: number;
    establishment_name: string;
    city: string | null;
    nb_rooms: number | null;
    current_pms: string | null;
    contact_name: string | null;
    contact_role: string | null;
    contact_email: string | null;
    contact_phone: string | null;
    is_decision_maker: boolean;
    is_unhappy_with_tools: boolean;
    asked_for_demo: boolean;
    verdict: 'demo' | 'nurturing';
    handoff: 'accompanying' | 'callback' | null;
    notes: string | null;
    status: 'pending' | 'validated' | 'rejected';
    rejected_reason: string | null;
    resolved_at: string | null;
    /** Qui a tranché (validé, écarté, rouvert) — posé en `whenLoaded` côté API : absent si la relation n'est pas chargée (mutations `reject`/`reopen`, voir `CrmEventIntakeMutationRow`). */
    resolved_by?: { id: number; name: string } | null;
    captured_at: string;
    client_uuid: string;
    partner?: { id: number; name: string; company: string | null };
    event: { id: number; name: string; type: CrmEventType };
    interest_ids?: number[];
    duplicate_ids: number[];
};

/**
 * Le sas de saisies de prestataire, tous salons confondus : en attente, plus
 * les refusées des 7 derniers jours (fenêtre posée par le serveur).
 */
export function useCrmEventIntakes(enabled = true) {
    return useCrmCollection<CrmEventIntakeRow>('/crm/event-intakes', { enabled });
}

/**
 * Nombre de saisies en attente, pour la pastille de l'entrée de menu.
 *
 * `useStaffMenuSections` tourne dans le layout : cette requête part depuis
 * TOUTE page super-admin, pas seulement l'écran « Demandes ». Un endpoint
 * dédié (`pendingCount`, même famille que `/crm/tasks/urgent-count`) qui ne
 * charge aucune relation ni ne calcule aucun doublon, plutôt que de rejouer
 * `/crm/event-intakes` (eager loading `partner`/`interests`/`event` +
 * `annotateDuplicates()` sur toute la file) pour n'en garder qu'un compte.
 */
export function useCrmPendingIntakesCount(enabled = true, refetchInterval: number | false = 60000) {
    return useApiQuery<{ count: number }>('/crm/event-intakes/pending-count', {}, {
        enabled,
        refetchInterval,
    });
}

/**
 * Forme réellement renvoyée par `reject()`/`reopen()` : le contrôleur ne
 * recharge ni l'événement ni le prestataire (voir `CrmIntakeQueueController`),
 * contrairement à `index()`, qui les eager-charge toujours. `event` y est
 * donc optionnel — `CrmEventIntakeRow` mentirait sur la forme réelle de cette
 * réponse. Personne ne s'en sert aujourd'hui pour patcher le cache
 * (`onSuccess` invalide et relit), mais un futur patch depuis cette réponse
 * ne doit pas planter sans que `tsc` ne l'ait vu venir.
 */
export type CrmEventIntakeMutationRow = Omit<CrmEventIntakeRow, 'event'> & { event?: CrmEventIntakeRow['event'] };

/**
 * Valide, refuse ou rouvre une saisie depuis le CRM.
 *
 * Volontairement PAS de recomposition de ligne à partir de la réponse : celle
 * de `reject`/`reopen` ne recharge ni l'événement ni le prestataire (voir
 * `CrmIntakeQueueController`), donc s'en servir pour patcher le cache viderait
 * ces colonnes sous les yeux de l'utilisateur. `onSuccess` invalide la liste
 * pour la faire relire en entier ; les 409/422 restent gérés par l'appelant,
 * qui décide s'il doit aussi relire (conflit) ou seulement expliquer (démo).
 */
export function useValidateCrmIntake() {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: CrmEventIntakeRow; lead_id: number }, ApiError, { id: number }>(
        'post',
        ({ id }) => `/crm/event-intakes/${id}/validate`,
        { onSuccess: () => invalidateCrmQueries(queryClient) },
    );
}

export function useRejectCrmIntake() {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: CrmEventIntakeMutationRow }, ApiError, { id: number; reason?: string }>(
        'post',
        ({ id }) => `/crm/event-intakes/${id}/reject`,
        { onSuccess: () => invalidateCrmQueries(queryClient) },
    );
}

export function useReopenCrmIntake() {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: CrmEventIntakeMutationRow }, ApiError, { id: number }>(
        'post',
        ({ id }) => `/crm/event-intakes/${id}/reopen`,
        { onSuccess: () => invalidateCrmQueries(queryClient) },
    );
}

/** Filtres de l'onglet « Toutes les demandes » : événement (salon ou webinaire), canal, température, démo, recherche libre. */
export type CrmEventLeadFilters = {
    event_id?: number;
    channel?: SalonLeadChannel;
    temperature?: SalonTemperature;
    demo?: SalonDemoMode;
    q?: string;
    page?: number;
};

/** Toutes les rencontres (et inscriptions webinaire), tous événements confondus, paginées (`ListCrmEventLeadsController`). */
export function useCrmEventLeadsList(filters: CrmEventLeadFilters) {
    const params = new URLSearchParams();
    if (filters.event_id) params.set('event_id', String(filters.event_id));
    if (filters.channel) params.set('channel', filters.channel);
    if (filters.temperature) params.set('temperature', filters.temperature);
    if (filters.demo) params.set('demo', filters.demo);
    if (filters.q) params.set('q', filters.q);
    if (filters.page && filters.page > 1) params.set('page', String(filters.page));

    const qs = params.toString();

    return useApiQuery<ApiResponse<CrmEventLead[]>>(`/crm/event-leads${qs ? `?${qs}` : ''}`);
}
